<template>
    <div class="scenario-detail flex flex-col flex-auto">
        <!-- Header -->
        <div class="pl-3 md:pl-10 flex-initial text-xs">
            <span>{{scenario.name}}</span>
            <img
            class="ml-3 inline align-top cursor-pointer"
            src="@/assets/images/SETTINGS.svg"
            @click="showSettingsPane()">
        </div>

        <!-- Node toolbar and buttons -->
        <div class="pl-3 md:pl-10 flex flex-row justify-between">
            <div class="dock pb-1 flex justify-start overflow-auto" ref="dock"></div>
            <div class="scenario-buttons pr-8 flex flex-col justify-center">
                <button
                class="twn-button text-xs mb-2"
                type="button"
                :disabled="hasPendingRequest"
                @click="saveScenario">Enregistrer</button>
                <button
                @click="previewScenario"
                class="twn-button text-xs mb-2"
                type="button"
                :disabled="hasPendingRequest || !itemID">Aperçu</button>
            </div>
        </div>

        <!-- Node editor -->
        <div class="flex flex-auto">
            <div
            class="flex-auto editor"
            :class="{ 
                'node-details-fade': (displayNodeDetails < 2),
                'node-details-fade-out': (displayNodeDetails == 0),
            }"
            ref="editor"></div>
        </div>

        <div class="mr-auto mb-16 ml-4">
            <button type="button" class="twn-button text-xs" @click="showDeleteModal">Supprimer le scénario</button>
        </div>

        <!-- Right pane (settings, media/asset, ...) -->
        <RightPanel
        ref="rightPanel"
        :title="rightPanelTitle[currentPaneType]"
        @hide="didHideRightPanel">
            <!-- Asset/Media Pane -->
            <Medias
            v-show="currentPaneType == 'asset'"
            :select-only="true"
            :draggable-asset="true"
            :default-type-filter-slug="assetTypeFilter"
            @select-media="mediaSelected"></Medias>

            <!-- Items (Games, ...) List -->
            <List
            v-show="currentPaneType == 'item'"
            :listing="itemListing"
            :fields="itemListFields"
            :filters="itemListFilters"
            :searchText="itemSearchText"
            :mobileFields="itemListFields"
            :select-only="true"
            @select-item="itemSelected">
            </List>

            <!-- Settings Pane -->
            <div class="settings" v-show="currentPaneType == 'settings'">
                <div class="settings-control">
                    <label>Identifiant</label>
                    <input
                    class="twn-input"
                    type="text"
                    name="name"
                    ref="name"
                    v-model="scenario.name">
                </div>
                <div class="settings-control">
                    <label>Description</label>
                    <textarea
                    class="twn-input"
                    rows="6"
                    name="description"
                    v-model="scenario.description"></textarea>
                </div>
                <div class="settings-control">
                    <label>Lieu</label>
                    <v-select
                    class="twn-select"
                    name="location"
                    label="name"
                    :options="locationList"
                    :clearable="true"
                    v-model="scenario.location"
                    ></v-select>
                </div>
                <div class="settings-control">
                    <label>Type</label>
                    <v-select
                    class="twn-select"
                    name="type"
                    label="name"
                    :clearable="false"
                    :options="scenarioTypeList"
                    v-model="scenario.type"
                    ></v-select>
                </div>
                <div class="pt-4 text-xs text-right">
                    <button
                    type="button"
                    class="twn-button"
                    :disabled="hasPendingRequest"
                    @click="saveScenario">Enregistrer</button>
                </div>
            </div>
        </RightPanel>

        <b-modal ref="delete-modal-detail-scenario" class="bootstrap" centered hide-footer id="delete-modal-detail-scenario" hide-header>
            <div class="d-block text-center my-6 uppercase font-semibold">
                <h3>Confirmer la suppression</h3>
            </div>
            <div class="flex flex-row justify-evenly items-center">
                <button type="button" class="mt-4 twn-button" @click="$bvModal.hide('delete-modal-detail-scenario')">Retour</button>
                <button type="button" class="mt-4 twn-button danger" @click="deleteItem">Supprimer</button>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import { mapState } from 'vuex'

    import AreaPlugin from "rete-area-plugin"

    import initNodeEditor from "../node-editor"
    import List from '@/components/List'
    import RightPanel from '@/components/RightPanel'
    import Medias from "./Medias"
    
    import { FC_DELETE_ITEM } from '@/graphql/list'

    export default {
        name: 'ScenarioDetail',
        props: ['itemID'],
        components: {
            List,
            RightPanel,
            Medias
        },
        data() {
            return {
                // UI states
                pendingRequest: 0,
                currentPaneTarget: null,
                currentPaneType: false,
                rightPanelTitle: {
                    asset: "Bibliotheque de médias",
                    item: "Bibliotheque de mini-jeux",
                    settings: "Paramètres du scénario"
                },
                assetTypeFilter: null,
                nodeEditor: null,
                displayNodeDetails: 2,
                // Game list config
                itemListing: 'game',
                itemSearchText: 'Rechercher un mini-jeu',
                itemListFields: [
                    {
                        key:'name',
                        label: 'Nom',
                        sortable: true
                    },
                    {
                        key: 'type',
                        db: `type:gameType {
                            name
                        }`,
                        nested: 'name',
                        label: 'Type',
                        sortable: true,
                    }
                ],
                itemListFilters: [
                    {
                        key:'type',
                        label: 'Type'
                    }
                ],
                // Local scenario states
                scenario: {
                    id: null,
                    name: "Nouveau scénario",
                    description: "",
                    location: null,
                    type: null,
                    reteNodes: null,
                },
            }
        },
        computed: {
            hasPendingRequest() {
                return (this.pendingRequest > 0)
            },
            ...mapState({
                locationList: state => state.Locations.list,
                proposalList: state => state.Proposals.list,
                scenarioTypeList: state => state.Scenarios.type_list
            })
        },
        watch: {
            itemID: {
                async handler(val) {
                    // Load current scenario data, if needed
                    if (val) {
                        // todo: handle invalid uuid response
                        await this.dispatchRequest('Scenarios/getByID', val)

                        this.updateLocalScenarioData()
                    }
                },
                immediate: true
            },
            currentPaneType: {
                handler(paneType, oldPaneType) {
                    if (paneType != oldPaneType && oldPaneType == "settings") {
                        this.restoreSettings()
                    }
                },
                immediate: true
            },
        },
        async mounted() {
            // Load location, scenario, game, media and proposal list
            await this.dispatchRequest('Locations/getList')
            await this.dispatchRequest('Scenarios/getTypeList')
            await this.dispatchRequest('Games/getList')
            await this.dispatchRequest('Media/getList')
            await this.dispatchRequest('Proposals/getList')

            this.updateLocalScenarioData()

            // Media and PDF nodes events
            this.$el.addEventListener('select-asset', this.showAssetPane)
            this.$el.addEventListener('asset-selected', this.hideRightPanel)

            // Media nodes specific event
            this.$el.addEventListener('select-asset-media', this.showMediaPanel)

            // PDF nodes specific event
            this.$el.addEventListener('select-asset-pdf', this.showPDFPanel)

            // Scene nodes specific event
            this.$el.addEventListener('select-asset-scene', this.showScenePanel)

            // Interactive nodes specific event
            this.$el.addEventListener('select-asset-interactive', this.showInteractivePanel) //todo: improve by getting data from the control

            // Game nodes events
            this.$el.addEventListener('select-item', this.showItemPane)
            this.$el.addEventListener('item-selected', this.hideRightPanel)

            // Game nodes specific event
            this.$el.addEventListener('select-item-game', this.showGamePanel)

            // Load node and trigger types and init editor
            await this.dispatchRequest('Nodes/getTypeList')
            await this.dispatchRequest('Triggers/getTypeList')

            this.nodeEditor = await initNodeEditor(this.$refs.editor, this.$refs.dock, this.$store.state.Nodes.type_list)

            // Add event to handle node details on zoom
            this.nodeEditor.on('zoom', this.handleZoom)

            // Add event to handle connection drop on node (and not on a socket), to connect them to default output or input
            this.nodeEditor.on('connectiondrop', this.handleConnectionDrop)

            if (this.scenario.reteNodes) {
                await this.nodeEditor.fromJSON(this.scenario.reteNodes)
                AreaPlugin.zoomAt(this.nodeEditor)
            }

            // Show settings pane by default for new scenario
            if (!this.itemID) {
                this.showSettingsPane(true)
            }
        },
        beforeDestroy() {
            this.$el.removeEventListener('select-asset', this.showAssetPane)
            this.$el.removeEventListener('asset-selected', this.hideRightPanel)
            this.$el.removeEventListener('select-asset-media', this.hideRightPanel)
            this.$el.removeEventListener('select-asset-pdf', this.hideRightPanel)
        },
        methods: {
            showDeleteModal(){
                this.$refs['delete-modal-detail-scenario'].show();
            },
            async deleteItem(){
                await this.$apollo.query({
                    query: FC_DELETE_ITEM("scenarios"),
                    variables: {
                        id: this.itemID
                    }
                })
                this.$refs['delete-modal-detail-scenario'].hide();
                this.$router.go(-1);
            },
            async dispatchRequest(action_name, args = null) {// todo mixin ?
                this.pendingRequest += 1

                let response = null

                if (args) {
                    response = await this.$store.dispatch(action_name, args)
                } else {
                    response = await this.$store.dispatch(action_name)
                }

                this.pendingRequest -= 1

                return response
            },
            handleZoom({ translate, zoom, source }) {
                if (zoom <= 0.5 && this.displayNodeDetails > 1) {
                    // Hide details
                    this.displayNodeDetails = 1

                    // Node icon Animation
                    setTimeout(() => {
                        this.displayNodeDetails = 0
                    }, 100)
                } else if (zoom > 0.5 && this.displayNodeDetails < 1) {
                    this.displayNodeDetails = 1

                    // Node icon Animation
                    setTimeout(() => {
                        this.displayNodeDetails = 2
                    }, 500)
                }
            },
            handleConnectionDrop(io) {
                const node = this.nodeEditor.custom_selected_node
                if (node) {
                    // Consume event
                    this.nodeEditor.custom_selected_node = null

                    // Don't connect to self
                    if (io.node != node) {
                        if (io.connectTo) {
                            // Connect to default input, if not already connected to it
                            const defaultInput = node.inputs.get('previous')

                            if (defaultInput && !io.connectedTo(defaultInput)) {
                                if (!io.multipleConnections && io.hasConnection())
                                    this.nodeEditor.removeConnection(io.connections[0])

                                this.nodeEditor.connect(io, defaultInput)
                            }
                        } else {
                            // Connect to default output, if not already connected to it
                            const defaultOutput = node.outputs.get('next')

                            if (defaultOutput && !defaultOutput.connectedTo(io)) {
                                if (!io.multipleConnections && io.hasConnection())
                                    this.nodeEditor.removeConnection(io.connections[0])

                                if (!defaultOutput.multipleConnections && defaultOutput.hasConnection())
                                    this.nodeEditor.removeConnection(defaultOutput.connections[0])

                                this.nodeEditor.connect(defaultOutput, io)
                            }
                        }
                    }
                }
            },
            showAssetPane(event) {
                if (this.currentPaneTarget)
                    this.currentPaneTarget.dispatchEvent(new CustomEvent('set-selected', { bubbles: true, composed: true, detail: false }))

                this.currentPaneTarget = event.target
                this.currentPaneTarget.dispatchEvent(new CustomEvent('set-selected', { bubbles: true, composed: true, detail: true }))

                this.currentPaneType = "asset"
                this.$refs['rightPanel'].show()
            },
            showItemPane(event) {
                if (this.currentPaneTarget)
                    this.currentPaneTarget.dispatchEvent(new CustomEvent('set-selected', { bubbles: true, composed: true, detail: false }))

                this.currentPaneTarget = event.target
                this.currentPaneTarget.dispatchEvent(new CustomEvent('set-selected', { bubbles: true, composed: true, detail: true }))

                this.currentPaneType = "item"
                this.$refs['rightPanel'].show()
            },
            showSettingsPane(select = false) {
                if (this.currentPaneType == 'settings')
                    return
                
                // Show settings pane and focus scenario name
                this.currentPaneType = "settings"
                this.$refs['rightPanel'].show()
                this.$nextTick(() => {
                    if (this.$refs && this.$refs.name) {
                        this.$refs.name.focus()

                        if (select)
                            this.$refs.name.setSelectionRange(0, this.scenario.name.length)
                    }
                })
            },
            didHideRightPanel() {
                if (this.currentPaneTarget)
                    this.currentPaneTarget.dispatchEvent(new CustomEvent('set-selected', { bubbles: true, composed: true, detail: false }))

                this.currentPaneType = null
            },
            hideRightPanel() {
                this.$refs['rightPanel'].hide()
            },
            showMediaPanel() {
                this.assetTypeFilter = 'file_image'
            },
            showPDFPanel() {
                this.assetTypeFilter = 'file_pdf'
            },
            showScenePanel() {
                this.assetTypeFilter = 'asset_scene'
            },
            showInteractivePanel() {
                this.assetTypeFilter = 'asset_interactive'
            },
            showGamePanel() {
                this.itemListing = 'game'
                this.itemSearchText = 'Rechercher un minijeu'
            },
            mediaSelected(asset) {
                if (!this.currentPaneTarget)
                    return

                this.currentPaneTarget.dispatchEvent(new CustomEvent('set-asset', { bubbles: true, composed: true, detail: JSON.stringify(asset) }))
            },
            itemSelected(item) {
                if (!this.currentPaneTarget)
                    return

                this.currentPaneTarget.dispatchEvent(new CustomEvent('set-item', { bubbles: true, composed: true, detail: JSON.stringify(item) }))
            },
            saveScenario() {
                const editorData = this.nodeEditor.toJSON()
                const data = {
                    ...this.scenario,
                    nodes: (editorData && editorData.nodes ? editorData.nodes : {}),
                }

                this.dispatchRequest('Scenarios/save', data).then((response) => {
                    if (response.id) {
                        this.$router.push({
                            name: 'scenario-edit',
                            params: {
                                itemID: response.id
                            }
                        })
                    }

                    // Update node with new external node_content (proposal, etc...)
                    const nodeExtContent = response.nodeExtContent
                    if (nodeExtContent) {
                        for (var i = 0; i < this.nodeEditor.nodes.length; i++) {
                            const reteNode = this.nodeEditor.nodes[i]
                            if (nodeExtContent[reteNode.id] && nodeExtContent[reteNode.id].simple_proposal) {
                                reteNode.data.simple_proposal = nodeExtContent[reteNode.id].simple_proposal
                            }
                        }
                    }

                    this.hideRightPanel()
                })
            },
            previewScenario() {
                window.open(process.env.VUE_APP_FRONT_URL + 'scenario/' + this.itemID, '_blank');
            },
            restoreSettings() {
                if (!this.itemID)
                    return

                const remoteData = this.$store.state.Scenarios.items[this.itemID]

                if (!remoteData)
                    return

                this.scenario.name = remoteData.name
                this.scenario.description = remoteData.description

                if (remoteData.scenario_type_id) {
                    const type = this.scenarioTypeList.find((type) => {
                        if (type.id == remoteData.scenario_type_id) {
                            return true
                        }

                        return false
                    })

                    this.scenario.type = type
                }

                if (remoteData.scenarioLocations && remoteData.scenarioLocations.length > 0) {
                    const location = this.locationList.find((loc) => {
                        if (loc.id == remoteData.scenarioLocations[0].location_id) {
                            return true
                        }

                        return false
                    })

                    this.scenario.location = (location || null)
                }
            },
            updateLocalScenarioData() {
                const remoteData = this.$store.state.Scenarios.items[this.itemID] //todo: computed?

                if (!remoteData) {
                    this.scenario.id = null
                    this.scenario.name = "Nouveau scénario"
                    this.scenario.description = ""
                    this.scenario.location = null
                    this.scenario.type = (this.scenarioTypeList && this.scenarioTypeList.length > 0 ? this.scenarioTypeList.find(type => type.slug == 'scenario') : null)
                    return
                }

                this.scenario.id = remoteData.id
                this.scenario.name = remoteData.name
                this.scenario.description = remoteData.description
                
                if (remoteData.scenario_type_id) {
                    const type = this.scenarioTypeList.find((type) => {
                        if (type.id == remoteData.scenario_type_id) {
                            return true
                        }

                        return false
                    })

                    this.scenario.type = type
                }

                if (remoteData.scenarioLocations && remoteData.scenarioLocations.length > 0) {
                    const location = this.locationList.find((loc) => {
                        if (loc.id == remoteData.scenarioLocations[0].location_id) {
                            return true
                        }

                        return false
                    })

                    this.scenario.location = (location || null)
                }

                // Convert nodes from DB to Rete.js format
                if (remoteData.nodes && remoteData.nodes.length > 0) {
                    let data = {
                        id: "ScenarioEditor@1.0.0",
                    }

                    data.nodes = remoteData.nodes.reduce((dict, node) => {
                        // Init base values
                        let reteNode = {
                            id: node.id,
                            name: node.nodeType.slug,
                            data: {},
                            inputs: {},
                            outputs: {},
                            position: [
                                node.position_x,
                                node.position_y
                            ],
                        }

                        // Get title if needed
                        if (node.title) {
                            reteNode.data.title = node.title
                        }

                        // Get node content
                        reteNode.data[node.nodeType.slug] = node.node_content

                        // Load node content if needed
                        if (reteNode.name == 'simple_proposal' && node.node_content) {
                            const proposal = this.proposalList.find((proposal) => {
                                if (proposal.id == node.node_content) {
                                    return true
                                }

                                return false
                            })

                            if (proposal) {
                                reteNode.data.text = proposal.texte
                            }
                        }

                        // Get outputs and buttons data from triggers
                        let outputsData = node.triggers.reduce((data, trigger) => {
                            let output = {
                                connections: [
                                {
                                    node: trigger.triggered_node_id,
                                    input: "previous",
                                    data: {}
                                }
                                ]
                            }

                            let outputKey = null
                            switch (trigger.triggerType.slug) {// todo:
                                case "continue":
                                    outputKey = "next"
                                    break
                                case "scene_button":
                                    data.buttonCount += 1

                                    outputKey = "btn" + data.buttonCount
                                    
                                    data.buttons[outputKey] = (trigger.name || "")
                                    break
                                case "interactive_trigger":
                                    outputKey = trigger.name
                                    
                                    data.customTriggers.push(trigger)
                                    break
                            }
                            
                            if (outputKey)
                                data.outputs[outputKey] = output

                            return data
                        }, {
                            buttonCount: 0,
                            buttons: {},
                            customTriggers: [],
                            outputs: {},
                        })

                        reteNode.outputs = outputsData.outputs

                        if (outputsData.buttonCount > 0)
                            reteNode.data.buttons = outputsData.buttons

                        if (outputsData.customTriggers.length > 0)
                            reteNode.data.customTriggers = outputsData.customTriggers

                        dict[node.id] = reteNode

                        return dict
                    }, {})

                    this.scenario.reteNodes = data

                    if (this.nodeEditor) {
                        this.nodeEditor.fromJSON(data).then(() => {
                            AreaPlugin.zoomAt(this.nodeEditor)
                        })
                    }
                }
            }
        }
    }
</script>

<style lang="scss">
// Fix bootstrap conflict
.node.media {
    display: block;
}

.dock {
    .item {
        @apply p-3;

        .node {
            cursor: grab;
            width: 240px;
            min-width: 240px;

            &:active {
                cursor: grabbing;
            }
        }
    }

    .title {
        margin-top: 10px;
    }

    .input, .outputs, .control, .cross-container, .button {
        display: none;
    }
}

.editor {
    cursor: move;

    .node .description {
        display: none;
    }

    .node {
        &:before {
            @apply absolute inset-0;
            display: none;
            content: '';
            z-index: 1;
            transition: opacity 0.5s;
            background-repeat: no-repeat;
            background-size: 70% 60%;
            background-position: center;
            background-color: white;
        }

        &.scene:before {
            background-image: url('../assets/images/SCENE_NODE.svg');
        }
        &.media:before {
            background-image: url('../assets/images/MEDIA_NODE.svg');
        }
        &.pdf:before {
            background-image: url('../assets/images/PDF_NODE.svg');
        }
        &.game:before {
            background-image: url('../assets/images/GAME_NODE.svg');
        }
        &.simple_proposal:before {
            background-image: url('../assets/images/TEXT_NODE.svg');
        }
        &.interactive:before {
            background-image: url('../assets/images/INTERACTIVE_NODE.svg');
        }

        .outputs {
            transition: background 0.5s;
            transition: border 0.5s;
        }
    }

    &.node-details-fade .node {
        &:before {
            display: block;
            opacity: 0;
        }
    }

    &.node-details-fade-out .node {
        &:before {
            display: block;
            opacity: 1;
        }

        .outputs {
            border-color: transparent;
            background: transparent;
        }
    }

    .connection {

        .main-path {
            cursor: no-drop;
            stroke-width: 4px;
            stroke: #d8d8d8;
        }

        &.input- {
            .main-path {
                stroke: #dc9799;
            }
        }
    }
}

.right-pane {
    top: 4rem;
    border-left: 1px solid rgba(0,0,0,0.2);
}

.settings {
    .settings-control {
        @apply mb-6;

        & > label {
            @apply mb-1 text-xs;
            display: block;
        }

        & > input, & > textarea {
            @apply w-full;
        }

        & > textarea {
            resize: none;
        }
    }
}
</style>